import { ref, readonly } from "vue";
import type {
  AdminManualBenefitCreate,
  PatchedAdminManualBenefitUpdate,
} from "@/api/model";
import {
  createManualBenefit,
  updateManualBenefit,
  deleteManualBenefit,
} from "../api";

export function useManualBenefit() {
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  async function create(
    profileId: number,
    data: AdminManualBenefitCreate
  ): Promise<boolean> {
    isLoading.value = true;
    error.value = null;

    try {
      const result = await createManualBenefit(profileId, data);

      if ("error" in result) {
        error.value = result.error;
        return false;
      }

      return true;
    } catch (err) {
      error.value = "Failed to create manual benefit";
      console.error("Create manual benefit error:", err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function update(
    profileId: number,
    benefitId: string,
    data: PatchedAdminManualBenefitUpdate
  ): Promise<boolean> {
    isLoading.value = true;
    error.value = null;

    try {
      const result = await updateManualBenefit(profileId, benefitId, data);

      if ("error" in result) {
        error.value = result.error;
        return false;
      }

      return true;
    } catch (err) {
      error.value = "Failed to update manual benefit";
      console.error("Update manual benefit error:", err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function remove(
    profileId: number,
    benefitId: string
  ): Promise<boolean> {
    isLoading.value = true;
    error.value = null;

    try {
      const result = await deleteManualBenefit(profileId, benefitId);

      if (result && "error" in result) {
        error.value = result.error;
        return false;
      }

      return true;
    } catch (err) {
      error.value = "Failed to delete manual benefit";
      console.error("Delete manual benefit error:", err);
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    isLoading: readonly(isLoading),
    error: readonly(error),
    create,
    update,
    remove,
  };
}
